import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { FaLocationPin, FaMapLocationDot } from "react-icons/fa6";
import { IoLocationOutline, IoAddCircleOutline } from 'react-icons/io5';
import { BiCurrentLocation } from 'react-icons/bi';
import { MdHome, MdWork, MdWorkOutline } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import NewAddress from '../address/NewAddress';
import Location from './Location';
import api from '../../api/api';
import { Modal } from 'antd';
import { setAddress, setSelectedAddress } from "../../model/reducer/addressReducer";
import './addressSelector.css';
import { setCity } from '../../model/reducer/locationReducer';
import { setShop } from '../../model/reducer/shopReducer';
import { toast } from 'react-toastify';

const AddressSelector = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showNewAddressModal, setShowNewAddressModal] = useState(false);
    const [isAddressSelected, setIsAddressSelected] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [locModal, setLocModal] = useState(false);
    const [isLocationPresent, setIsLocationPresent] = useState(false);
    const [isFetchingCity, setIsFetchingCity] = useState(false);
    const user = useSelector(state => state.user);
    const address = useSelector(state => state.address);
    const settings = useSelector(state => state.setting);
    const city = useSelector(state => state.city);
    const hasFetchedCity = useRef(false);
    const [showPermissionDenied, setShowPermissionDenied] = useState(false);
    const [locationPermission, setLocationPermission] = useState("prompt");

    useEffect(() => {
        if (user?.jwtToken && user?.jwtToken !== "") {
            fetchAddresses();
        }
    }, [user?.jwtToken]);

    useEffect(() => {
        if (
            address?.selected_address &&
            !hasFetchedCity.current &&
            address?.selected_address?.latitude &&
            address?.selected_address?.longitude
        ) {
            hasFetchedCity.current = true;
            fetchCity();
        }
    }, [address?.selected_address, hasFetchedCity.current]);

    useEffect(() => {
        checkLocationPermission();
    }, []);

    const checkLocationPermission = () => {
        if (!("geolocation" in navigator)) {
            setLocationPermission("denied");
            setShowPermissionDenied(true);
            return;
        }

        if (navigator.permissions) {
            navigator.permissions
                .query({ name: "geolocation" })
                .then((permissionStatus) => {
                    setLocationPermission(permissionStatus.state);
                    if (permissionStatus.state === "prompt" || permissionStatus.state === "denied") {
                        setShowPermissionDenied(true);
                    } else {
                        setShowPermissionDenied(false);
                    }

                    // Listen for permission changes
                    permissionStatus.onchange = () => {
                        setLocationPermission(permissionStatus.state);
                        if (permissionStatus.state === "granted") {
                            setShowPermissionDenied(false);
                        } else if (permissionStatus.state === "denied") {
                            setShowPermissionDenied(true);
                        }
                    };
                });
        }
    };

    const fetchCity = async () => {
        try {
            setIsFetchingCity(true);
            const response = await api.getCity(address?.selected_address?.latitude, address?.selected_address?.longitude);
            const result = await response.json();
            if (result.status === 1) {
                dispatch(setCity({
                    data: {
                        id: result.data.id,
                        name: result.data.name,
                        state: result.data.state,
                        formatted_address: result.data.formatted_address,
                        latitude: result.data.latitude,
                        longitude: result.data.longitude,
                        min_amount_for_free_delivery:
                            result.data.min_amount_for_free_delivery,
                        delivery_charge_method: result.data.delivery_charge_method,
                        fixed_charge: result.data.fixed_charge,
                        per_km_charge: result.data.per_km_charge,
                        time_to_travel: result.data.time_to_travel,
                        max_deliverable_distance: result.data.max_deliverable_distance,
                        distance: result.data.distance,
                    },
                }));

                // Fetch shop data after city is set
                fetchShop(result.data.latitude, result.data.longitude);
            }
            else if (result?.message == "We doesn't delivery at selected city") {
                dispatch(setCity({
                    data: {
                        latitude: 0,
                        longitude: 0,
                        formatted_address: `${address?.selected_address?.city}, ${address?.selected_address?.state}, ${address?.selected_address?.country}`,
                    }
                }))
                toast.error(result?.message)
                setIsAddressSelected(true)
            }
        } catch (error) {
            console.error("Error fetching city:", error);
        } finally {
            setIsFetchingCity(false);
        }
    }

    const fetchShop = async (latitude, longitude) => {
        try {
            const response = await api.getShop(latitude, longitude, user?.jwtToken);
            const result = await response.json();
            if (result?.status == 1) {
                dispatch(setShop({ data: result?.data }));
            }
        } catch (error) {
            console.log(error?.message);
        }
    };

    useEffect(() => {
        if (city?.city) {
            setIsLocationPresent(true);
        }
    }, [city]);

    const fetchAddresses = async () => {
        setIsLoading(true);
        try {
            const response = await api.getAddress(user?.jwtToken);
            const result = await response.json();
            if (result.status === 1) {
                setAddresses(result.data);
                dispatch(setAddress({ data: result.data }));
                // Set default address if available
                const defaultAddress = result.data.find(addr => addr.is_default === 1);
                if (defaultAddress) {
                    dispatch(setSelectedAddress({ data: defaultAddress }));
                }
            }
        } catch (error) {
            console.error("Error fetching addresses:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (address?.selected_address) {
            setIsAddressSelected(true);
        }
    }, [address?.selected_address]);

    // Open the location modal when detect current location is clicked
    const handleLocationClick = () => {
        setLocModal(true);
    };

    const setBodyScroll = (value) => {
        if (value) {
            document.body.style.overflow = 'auto';
            document.body.style.height = '100vh';
        } else {
            document.body.style.overflow = 'auto';
            document.body.style.height = 'auto';
        }
    };

    const getAddressIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'home':
                return <MdHome className="address-type-icon" />;
            case 'office':
                return <MdWorkOutline className="address-type-icon" />;
            default:
                return <IoLocationOutline className="address-type-icon" />;
        }
    };

    const handleNewAddressClick = () => {
        setShowNewAddressModal(true);
    };

    return (
        <div id="address-selector-container">
            <Dropdown>
                <Dropdown.Toggle className="address-selector-toggle">
                    <div className="location-icon-wrapper">
                        <IoLocationOutline size={24} className="location-icon" />
                    </div>
                    <div className="location-info">
                        <span className="location-label">{t("deliver_to")}</span>
                        <span className="current-location">
                            {city?.status == "fulfill" && isAddressSelected == false
                                ? <span className="selected-address-text">
                                    {city?.city?.formatted_address}
                                </span>
                                : isAddressSelected == true
                                    ? <span className="selected-address-text">
                                        {address?.selected_address?.city}, {address?.selected_address?.state}, {address?.selected_address?.country}
                                    </span>
                                    : t("select_location")
                            }
                        </span>
                    </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="address-selector-menu">
                    <div className="address-selector-header">
                        <button
                            className="detect-location-btn"
                            onClick={handleLocationClick}
                        >
                            <BiCurrentLocation />
                            <span>{t("detect_current_location")}</span>
                            <span className="using-gps">{t("using_GPS")}</span>
                        </button>

                        <button
                            className="add-address-btn"
                            onClick={handleNewAddressClick}
                        >
                            <IoAddCircleOutline />
                            <span>{t("add_address")}</span>
                        </button>
                    </div>

                    {addresses.length > 0 && (
                        <div className="saved-addresses-section">
                            <h6 className="saved-addresses-title">{t("saved_addresses")}</h6>
                            <div className="addresses-list">
                                {addresses.map((addr) => (
                                    <Dropdown.Item
                                        key={addr.id}
                                        className="address-item"
                                        onClick={() => {
                                            hasFetchedCity.current = false;
                                            dispatch(setSelectedAddress({ data: { ...addr, is_default: 1 } }));
                                            setIsAddressSelected(true);
                                        }}
                                    >
                                        {getAddressIcon(addr.type)}
                                        <div className="address-details">
                                            <span className="address-type">{addr.type}</span>
                                            <span className="address-text">
                                                {addr.city}, {addr.state}, {addr.country}
                                            </span>
                                        </div>
                                    </Dropdown.Item>
                                ))}
                            </div>
                        </div>
                    )}
                </Dropdown.Menu>
            </Dropdown>

            {/* New Address Modal */}
            <NewAddress
                show={showNewAddressModal}
                setshow={setShowNewAddressModal}
                isAddressSelected={false}
                setIsAddressSelected={() => { }}
                isLoader={isLoading}
                setisLoader={setIsLoading}
                setAddresses={setAddresses}
            />

            {/* Location Modal */}
            <Modal
                className='location'
                id="locationModal"
                centered
                open={locModal}
                transitionName=''
                closable={true}
                maskClosable={true}
                onCancel={() => setLocModal(false)}
            >
                <Location
                    isLocationPresent={isLocationPresent}
                    setisLocationPresent={setIsLocationPresent}
                    showModal={locModal}
                    setLocModal={setLocModal}
                    bodyScroll={setBodyScroll}
                    setIsAddressSelected={setIsAddressSelected}
                    addresses={addresses}
                    showPermissionDenied={showPermissionDenied}
                    setShowPermissionDenied={setShowPermissionDenied}
                />
            </Modal>
        </div>
    );
};

export default AddressSelector; 